import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@timeghost/ui/shadcn/tooltip";
import { type ComponentProps, cloneElement, isValidElement, type JSX, type ReactElement, type ReactNode } from "react";

type TooltipSide = "top" | "bottom" | "left" | "right" | "inline-start" | "inline-end";
type TooltipAlign = "start" | "center" | "end";

export interface TipProps
	extends Omit<
		ComponentProps<typeof TooltipTrigger>,
		"children" | "className" | "content" | "delay" | "disabled" | "render"
	> {
	readonly align?: TooltipAlign;
	readonly children: ReactNode;
	readonly className?: string;
	readonly content: ReactNode;
	readonly delayDuration?: number;
	readonly disabled?: boolean;
	readonly open?: boolean;
	readonly showArrow?: boolean;
	readonly side?: TooltipSide;
	readonly sideOffset?: number;
}

/**
 * Quick tooltip wrapper around shadcn Tooltip.
 * @example
 * <Tip content="Show booked"><Button>...</Button></Tip>
 * <Tip content={<span>Rich <strong>tip</strong></span>}>...</Tip>
 */
export default function Tip({
	content,
	delayDuration = 500,
	children,
	open,
	side = "top",
	align = "center",
	sideOffset = 4,
	disabled = false,
	className,
	showArrow = true,
	...triggerProps
}: TipProps): JSX.Element {
	const hasContent = content !== null && content !== undefined && content !== false && content !== "";

	// Props injected by a parent `render` slot (Popover/Dropdown triggers) must reach the child element.
	if (!hasContent || disabled) {
		if (isValidElement(children)) {
			return cloneElement(children as ReactElement<Record<string, unknown>>, triggerProps);
		}
		return <>{children}</>;
	}

	const trigger = isValidElement(children) ? (
		<TooltipTrigger {...triggerProps} delay={delayDuration} render={children as ReactElement} />
	) : (
		<TooltipTrigger {...triggerProps} delay={delayDuration} render={<span className="inline-flex max-w-full" />}>
			{children}
		</TooltipTrigger>
	);

	return (
		<TooltipProvider delay={delayDuration}>
			<Tooltip open={open}>
				{trigger}
				<TooltipContent align={align} className={className} showArrow={showArrow} side={side} sideOffset={sideOffset}>
					{content}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
